import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { PrivatesAppointmentsApi } from "@/openapi/generated";
import { createAuthenticatedApi } from "@/lib/utils";
import { appointmentBookingsQueryKey } from "../utils/appointment-bookings-query";

interface IUseAppointmentBookingsGetProps {
  appointmentId: number;
  enabled?: boolean;
  /** Admin list keeps defaults (always remount-refetch). Booking modal can honor page cache. */
  staleTime?: number;
  refetchOnMount?: boolean | "always";
}

export const useAppointmentBookingsGet = ({
  appointmentId,
  enabled = true,
  staleTime = 0,
  refetchOnMount = true,
}: IUseAppointmentBookingsGetProps) => {
  const accessToken = useAccessToken();

  const { data, isError, isLoading, isSuccess, isPending, refetch } = useQuery({
    queryKey: appointmentBookingsQueryKey(appointmentId),
    queryFn: () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(
        PrivatesAppointmentsApi,
        accessToken,
      );
      return service.apiAppointmentsIdBookingsGet({
        id: appointmentId,
      });
    },
    enabled: enabled && appointmentId > 0 && !!accessToken,
    refetchOnMount,
    refetchOnWindowFocus: false,
    staleTime,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    refetch,
  };
};
